import React, { useCallback, useRef, useMemo, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Button } from 'devextreme-react/button';
import {
    Column,
    DataGrid,
    Scrolling,
    FilterRow,
    Editing,
    Export,
    Selection,
    Toolbar as DataGridToolbar,
    Item as DataGridToolbarItem,
    RequiredRule,
    StateStoring,
    ColumnChooser
} from "devextreme-react/data-grid";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import axios from "axios";
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import RenderSelectBoxCell from '../../../../common/RenderCell';
import GridLayoutManager from '../../../../gridLayoutCustomization/GridLayoutManager';
import { GridLayout } from '../../../../common/GridLayout';
import { createConcurrencyAwareStore, refreshGridOnConflict } from '../../../../common/dataStore';
import data from "../../../../assets/apiRoutes.json";

// Storage Charges Grid Component
const StorageChargesGrid = forwardRef(({ itemsGridRef, accessToken, chargesGridRef, selectedItemKeys, setSelectedItemKeys, currentLayout, setCurrentLayout }, ref) => {
    const gridKey = "DFD4C0CB-D2AA-4FD2-9D3A-91DD0C1CC5FB";
    const isFormLoadRef = useRef(true);
    const detailIdRef = useRef(null);
    const previousDetailIdRef = useRef(null);
    
    // Expose method to update detailIdRef from parent
    useImperativeHandle(ref, () => ({
        updateDetailId: (detailId) => {
            if (detailId && detailId !== detailIdRef.current) {
                detailIdRef.current = detailId;
                previousDetailIdRef.current = detailId;
                // Refresh the grid with new detail ID using setTimeout to prevent Items Grid refresh
                setTimeout(() => {
                    if (chargesGridRef.current && chargesGridRef.current.instance && typeof chargesGridRef.current.instance === 'function') {
                        chargesGridRef.current.instance().refresh();
                    }
                }, 0);
            }
        }
    }));
    
    // Get selected detail ID from Items Grid ref (doesn't cause re-render)
    // Only used in onInitNewRow as fallback
    const getSelectedDetailId = useCallback(() => {
        if (itemsGridRef.current && itemsGridRef.current.instance && typeof itemsGridRef.current.instance === 'function') {
            try {
                const selectedKeys = itemsGridRef.current.instance().getSelectedRowKeys();
                return selectedKeys && selectedKeys.length > 0 ? selectedKeys[0] : null;
            } catch (error) {
                return null;
            }
        }
        return null;
    }, [itemsGridRef]);
    
    const url = "wm/TransactionCharge/GetTransactionChargeDetailsByDetailId";
    const updateUrl = "wm/TransactionCharge/UpdateTransactionChargeDetails";
    const insertUrl = "wm/TransactionCharge/AddTransactionChargeDetailsByDetailId";
    const deleteUrl = "wm/TransactionCharge/DeleteTransactionChargeDetails";

    const dataSource = useMemo(() => createStore({
        key: 'rowid',
        loadUrl: `${window._env_.REACT_APP_APIS}/${url}`,
        insertUrl: `${window._env_.REACT_APP_APIS}/${insertUrl}`,
        updateUrl: `${window._env_.REACT_APP_APIS}/${updateUrl}`,
        deleteUrl: `${window._env_.REACT_APP_APIS}/${deleteUrl}`,
        onBeforeSend: function (method, ajaxOptions) {
            // Don't call getSelectedDetailId here - it reads from Items Grid and causes refresh
            // detailIdRef is updated from parent via ref method
            ajaxOptions.headers = {
                Authorization: `Bearer ${accessToken}`,
                Key: detailIdRef.current || '00000000-0000-0000-0000-000000000000',
                "FormLoad": isFormLoadRef.current,
            };
        },
        onUpdated: function (e) {
            isFormLoadRef.current = false;
        },
        onInserted: function (e) {
            isFormLoadRef.current = false;
        }
    }), [accessToken]);

    const saveState = useCallback((state) => { 
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout); 
    }, [gridKey, currentLayout, accessToken]);
    
    const loadState = useCallback(() => { 
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout); 
    }, [currentLayout, accessToken, gridKey]);

    const handleCellValueChanged = (data) => {
        setSelectedItemKeys(data.selectedRowKeys);
    };

    const deleteRecords = useCallback(() => {
        let result = confirm("<i>Are you sure you want to delete selected records?</i>", "Delete");
        result.then((dialogResult) => {
            if (dialogResult) {
                chargesGridRef.current.instance().beginCustomLoading("Deleting");
                axios.post(
                    `${window._env_.REACT_APP_APIS}/${deleteUrl}`,
                    selectedItemKeys,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`
                        },
                    }
                )
                .then((res) => {
                    chargesGridRef.current.instance().endCustomLoading();
                    chargesGridRef.current.instance().refresh();
                })
                .catch((err) => {
                    chargesGridRef.current.instance().endCustomLoading();
                    notify(
                        {
                            message: "Error deleting charges",
                            width: 300,
                        },
                        'error',
                        3000,
                    );
                });
            }
        });
    }, [selectedItemKeys, accessToken, deleteUrl, chargesGridRef]);

    const onInitNewRow = useCallback((e) => {
        // Get current detail ID from ref when inserting
        const currentDetailId = getSelectedDetailId();
        if (currentDetailId) {
            e.data.rTransactiondetail = currentDetailId;
            detailIdRef.current = currentDetailId;
        }
        e.data.chargetype = 'S'; // Storage charge type
    }, [getSelectedDetailId]);

    const onContentReady = useCallback((e) => {
        if (!e.component._hasLoaded) {
            e.component._hasLoaded = true;
            var selection = e.component.getSelectedRowKeys();
            if (selection.length == 0) {
                e.component.selectRowsByIndexes([0]);
            }
        }
        // Don't call getSelectedDetailId here - it reads from Items Grid and causes re-renders
        // The detailIdRef will be updated in onInitNewRow when a new row is inserted
        // For existing data, the detailIdRef should already be set from the dataSource Key header
    }, []);

    // Don't check detail ID during render - let onContentReady handle it
    // This prevents re-renders that cause selection loss

    return (
        <div>
            <DataGrid
                onExporting={(e) => {
                    if (window.handleDevExtremeDataGridExport) {
                        window.handleDevExtremeDataGridExport(e);
                    }
                }}
                dataSource={dataSource}
                ref={chargesGridRef}
                selectedRowKeys={selectedItemKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={handleCellValueChanged}
                onContentReady={onContentReady}
                onRowInserting={onInitNewRow}
                height="30vh"
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <Scrolling mode="standard" />
                <Selection mode="single" />
                <Export enabled={true} allowExportSelectedData={true} />
                <ColumnChooser enabled={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowUpdating={true}
                    allowAdding={true}
                />
                <FilterRow visible={true} />
                <Column dataField="chargeCode" caption="Charge Code" 
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/wm/Lookup/GetTransactionCharges`,
                            loadParams: {
                                chargeTypes: function () {
                                    return 'S';
                                }
                            },
                            key: "id",
                            displayExpr: "name",
                            valueExpr: "id",
                            accessToken: accessToken,
                            defaultValue: cellElement.data.rCharge,
                            sort: [{ selector: "name", desc: false }],
                            onValueChangeCallback: (value, text, additionalData) => {
                                cellElement.setValue(({ value, text, additionalData }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.rCharge = newValue.value;
                        rowData.chargeCode = newValue.text;
                        if (newValue.additionalData) {
                            rowData.description = newValue.additionalData.description || rowData.description;
                            rowData.chargeuom = newValue.additionalData.chargeuom || rowData.chargeuom;
                        }
                    }}
                >
                    <RequiredRule message="Charge is required." />
                </Column>
                <Column dataField="description" caption="Description" allowEditing={false} />
                <Column dataField="chargetiming" caption="Charge Timing" dataType="string"  allowEditing={false} visible={false} />
                <Column dataField="chargereleasedate" caption="Release Date" dataType="date" allowSorting={false} visible={false} />
                <Column dataField="quantitybillas" caption="Bill As" dataType="number" format="#,##0.00"  />
                <Column dataField="quantitybillasuom" caption="UOM" dataType="string"  />
                <Column dataField="rate" caption="Rate" dataType="number" 
                    format={{
                        type: 'currency',
                        precision: 2,
                        currency: 'USD',
                        useGrouping: true
                    }} />
                <Column dataField="amount" caption="Amount" dataType="number" 
                    format={{
                        type: 'currency',
                        precision: 2,
                        currency: 'USD',
                        useGrouping: true
                    }} allowEditing={false} />
                <Column  dataField="userdefinedcode1" caption="Code1" dataType="string" 
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.WMLookup.GetCodes}`,
                            loadParams: {
                                codeType: function () {
                                    return '36';
                                }
                            },
                            key: "name",
                            displayExpr: "name",
                            valueExpr: "name",
                            accessToken: accessToken,
                            sort: [{ selector: "name", desc: false }],
                            defaultValue: cellElement.data.userdefinedcode1,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.userdefinedcode1 = newValue.text;
                    }}
                />
                <Column  dataField="userdefinedcode2" caption="Code2" dataType="string" 
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.WMLookup.GetCodes}`,
                            loadParams: {
                                codeType: function () {
                                    return '37';
                                }
                            },
                            key: "name",
                            displayExpr: "name",
                            valueExpr: "name",
                            accessToken: accessToken,
                            sort: [{ selector: "name", desc: false }],
                            defaultValue: cellElement.data.userdefinedcode2,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.userdefinedcode2 = newValue.text;
                    }}
                    allowEditing={false} visible={false}/>
                <Column  dataField="userdefinedcode3" caption="Code3" dataType="string" 
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.WMLookup.GetCodes}`,
                            loadParams: {
                                codeType: function () {
                                    return '38';
                                }
                            },
                            key: "name",
                            displayExpr: "name",
                            valueExpr: "name",
                            accessToken: accessToken,
                            sort: [{ selector: "name", desc: false }],
                            defaultValue: cellElement.data.userdefinedcode3,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.userdefinedcode3 = newValue.text;
                    }}
                    allowEditing={false} visible={false}/>
                <Column  dataField="userdefinedcode4" caption="Code4" dataType="string" 
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.WMLookup.GetCodes}`,
                            loadParams: {
                                codeType: function () {
                                    return '39';
                                }
                            },
                            key: "name",
                            displayExpr: "name",
                            valueExpr: "name",
                            accessToken: accessToken,
                            sort: [{ selector: "name", desc: false }],
                            defaultValue: cellElement.data.userdefinedcode4,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.userdefinedcode4 = newValue.text;
                    }}
                    allowEditing={false} visible={false}/>
                   <Column  dataField="userdefinedcode5" caption="Code5" dataType="string" 
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.WMLookup.GetCodes}`,
                            loadParams: {
                                codeType: function () {
                                    return '40';
                                }
                            },
                            key: "name",
                            displayExpr: "name",
                            valueExpr: "name",
                            accessToken: accessToken,
                            sort: [{ selector: "name", desc: false }],
                            defaultValue: cellElement.data.userdefinedcode4,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.userdefinedcode4 = newValue.text;
                    }}
                    allowEditing={false} visible={false}/>
                <Column visible={false} dataField="userdefineddate1" caption="Date1" dataType="date" allowEditing={false} />
                <Column visible={false} dataField="userdefineddate2" caption="Date2" dataType="date" allowEditing={false} />
                <Column visible={false} dataField="userdefineddate3" caption="Date3" dataType="date" allowEditing={false} />
                <Column visible={false} dataField="userdefineddate4" caption="Date4" dataType="date" allowEditing={false} />
                <Column visible={false} dataField="userdefineddate5" caption="Date5" dataType="date" allowEditing={false} />
                <Column visible={false} dataField="userdefinedquantity1" caption="Quantity1" dataType="number" format="#,##0.00" allowEditing={false} />
                <Column visible={false} dataField="userdefinedquantity2" caption="Quantity2" dataType="number" format="#,##0.00" allowEditing={false} />
                <Column visible={false} dataField="userdefinedquantity3" caption="Quantity3" dataType="number" format="#,##0.00" allowEditing={false} />
                <Column visible={false} dataField="userdefinedquantity4" caption="Quantity4" dataType="number" format="#,##0.00" allowEditing={false} />
                <Column visible={false} dataField="userdefinedquantity5" caption="Quantity5" dataType="number" format="#,##0.00" allowEditing={false} />
                <Column visible={false} dataField="userdefinedreference1" caption="Reference1" dataType="string" allowEditing={false} />
                <Column visible={false} dataField="userdefinedreference2" caption="Reference2" dataType="string" allowEditing={false} />
                <Column visible={false} dataField="userdefinedcomment" caption="Comment" dataType="string" allowEditing={false} />
                <Column visible={false} dataField="rGlaccount" caption="Account" dataType="string" allowEditing={false} />
                <Column visible={false} dataField="rCaactivity" caption="Activity" dataType="string" allowEditing={false} />
                <Column visible={false} dataField="rGldepartment" caption="Department" dataType="string" allowEditing={false} />
                <Column visible={false} dataField="rFafund" caption="Fund" dataType="string" allowEditing={false} />
                <Column visible={false} dataField="rCajob" caption="Job" dataType="string" allowEditing={false} />
                <DataGridToolbar>
                    <DataGridToolbarItem name="addRowButton" />
                    <DataGridToolbarItem location="after">
                        <Button
                            onClick={deleteRecords}
                            icon="trash"
                            disabled={!selectedItemKeys.length}
                        />
                    </DataGridToolbarItem>
                    <DataGridToolbarItem location="after">
                        <GridLayoutManager gridKey={gridKey} dataGrid={chargesGridRef} setCurrentLayout={setCurrentLayout} />
                    </DataGridToolbarItem>
                    <DataGridToolbarItem name="exportButton" />
                    <DataGridToolbarItem name="columnChooserButton" />
                </DataGridToolbar>
            </DataGrid>
        </div>
    );
});

// Storage Charges Tab Component
const StorageChargesTab = ({ 
    transactionHeaderId, 
    accessToken, 
    customerId,
    itemsGridRef, 
    chargesGridRef, 
    selectedItemsKeys, 
    setSelectedItemsKeys, 
    selectedChargesKeys, 
    setSelectedChargesKeys,
    currentItemsLayout,
    setCurrentItemsLayout,
    currentChargesLayout,
    setCurrentChargesLayout
}) => {
    const itemsGridKey = "47C39800-05B2-41FD-B603-B844A8EDC239";
    const chargesGridKey = "DFD4C0CB-D2AA-4FD2-9D3A-91DD0C1CC5FB";
    const isFormLoadRef = useRef(true);
    const storageChargesGridRef = useRef(null); // Ref to StorageChargesGrid component
    
    // Use a ref to track selection locally without causing re-renders
    const localSelectionRef = useRef([]);
    const isUpdatingChargesGridRef = useRef(false); // Flag to prevent Items Grid refresh during Charges Grid update
    
    // Initialize from parent state only once
    const [localSelectedItemsKeys, setLocalSelectedItemsKeys] = useState(() => selectedItemsKeys || []);

    // createConcurrencyAwareStore, not createStore — grids bypass axios, so without it a 409 from
    // UpdateTransactionDetail lands as a bare DevExtreme error row with no stale-record dialog. The
    // accessToken second argument is deliberately OMITTED (dataStore.js:81-83): the onBeforeSend below
    // replaces `ajaxOptions.headers` wholesale and carries a FormLoad header the API reads, so any injected
    // headers would be discarded anyway.
    const itemsDataSource = useMemo(() => createConcurrencyAwareStore({
        onConflict: refreshGridOnConflict(itemsGridRef),
        key: 'rowid',
        loadUrl: `${window._env_.REACT_APP_APIS}/${data.Transaction.GetTransactionDetails}`,
        insertUrl: `${window._env_.REACT_APP_APIS}/${data.Transaction.InsertTransactionDetail}`,
        updateUrl: `${window._env_.REACT_APP_APIS}/${data.Transaction.UpdateTransactionDetail}`,
        loadParams: {
            transactionRowId: transactionHeaderId || '00000000-0000-0000-0000-000000000000'
        },
        onBeforeSend: function (method, ajaxOptions) {
            ajaxOptions.headers = {
                Authorization: `Bearer ${accessToken}`,
                "FormLoad": isFormLoadRef.current,
            };
        },
        onUpdated: function (e) {
            isFormLoadRef.current = false;
        },
        onInserted: function (e) {
            isFormLoadRef.current = false;
        }
    }), [transactionHeaderId, accessToken]);

    if (!transactionHeaderId || transactionHeaderId === '00000000-0000-0000-0000-000000000000') {
        return <div style={{ padding: '20px', textAlign: 'center' }}>Please save the transaction first to view storage charges.</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '20px', padding: '10px' }}>
            <div style={{ flex: 1, minHeight: 0 }}>
                <h6 style={{ marginBottom: '10px', fontWeight: 'bold' }}>Items Grid</h6>
                <DataGrid
                    onExporting={(e) => {
                        if (window.handleDevExtremeDataGridExport) {
                            window.handleDevExtremeDataGridExport(e);
                        }
                    }}
                    dataSource={itemsDataSource}
                    ref={itemsGridRef}
                    selectedRowKeys={localSelectedItemsKeys}
                    showBorders={true}
                    rowAlternationEnabled={true}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    columnAutoWidth={true}
                    onSelectionChanged={(data) => {
                        // Prevent update if we're already updating the Charges Grid (prevents refresh loop)
                        if (isUpdatingChargesGridRef.current) {
                            return;
                        }
                        // Update local state immediately - this is what the grid uses
                        setLocalSelectedItemsKeys(data.selectedRowKeys);
                        // Update local ref
                        localSelectionRef.current = data.selectedRowKeys;
                        // Update detailIdRef in StorageChargesGrid via ref method (doesn't cause Items Grid refresh)
                        if (data.selectedRowKeys && data.selectedRowKeys.length > 0) {
                            const selectedId = data.selectedRowKeys[0];
                            if (storageChargesGridRef.current && storageChargesGridRef.current.updateDetailId) {
                                isUpdatingChargesGridRef.current = true;
                                storageChargesGridRef.current.updateDetailId(selectedId);
                                // Reset flag after Charges Grid refresh completes
                                setTimeout(() => {
                                    isUpdatingChargesGridRef.current = false;
                                }, 150);
                            }
                        }
                        // Don't update parent state to avoid re-renders
                    }}
                    onRowInserting={(e) => {
                        e.data['RTransactionheader'] = transactionHeaderId;
                        e.data['RecordType'] = 'R';
                    }}
                    onRowUpdating={(e) => {
                        // Carry the version the user was editing. Named-field copy only, never
                        // `{ ...e.oldData, ...e.newData }` — in cell edit mode e.newData holds just the
                        // changed cell, and widening it would widen what the server writes. Without this the
                        // server's token derives from the row's own current value, so the concurrency check
                        // compares the row against itself and silently fails open.
                        if (e.newData && !('concurrencyToken' in e.newData) && e.oldData) {
                            e.newData.concurrencyToken = e.oldData.concurrencyToken;
                        }
                    }}
                    height="30vh"
                >
                    <StateStoring
                        enabled={true}
                        type="custom"
                        customLoad={() => GridLayout.sendStorageRequest(itemsGridKey, "GET", accessToken, null, currentItemsLayout)}
                        storageKey={itemsGridKey}
                        customSave={(state) => GridLayout.sendStorageRequest(itemsGridKey, "POST", accessToken, state, currentItemsLayout)}
                    />
                    <Scrolling mode="standard" />
                    <Selection mode="single" />
                    <Export enabled={true} allowExportSelectedData={true} />
                    <ColumnChooser enabled={true} />
                    <Editing
                        mode="cell"
                        refreshMode="repaint"
                        allowUpdating={true}
                        allowAdding={true}
                    />
                    <FilterRow visible={true} />
                    <Column dataField="itemId" caption="Item Id" visible={false}  />
                    <Column dataField="itemCode" caption="Item Code" dataType="string" 
                        editCellRender={(cellElement) =>
                            RenderSelectBoxCell({
                                loadUrl: `${window._env_.REACT_APP_APIS}/${data.Item.GetItems}`,
                                loadParams: {
                                    customerIdentity: customerId || null
                                },
                                key: "RowId",
                                displayExpr: "ItemCode",
                                valueExpr: "RowId",
                                accessToken: accessToken,
                                sort: [{ selector: "ItemCode", desc: false }],
                                defaultValue: cellElement.data.itemCode,
                                onValueChangeCallback: (value, text) => {
                                    cellElement.setValue(({ value, text }));
                                },
                            })
                        }
                        setCellValue={(rowData, newValue) => {
                            rowData.itemCode = newValue.text;
                            rowData.itemId = newValue.value;
                        }}
                    />
                    <Column dataField="itemDesc1" caption="Item Description" dataType="string"  allowEditing={false} />
                    <Column dataField="lotId" caption="Lot Id" visible={false}  />
                    <Column dataField="lotCode" caption="Lot Code" dataType="string" 
                        editCellRender={(cellElement) =>
                            RenderSelectBoxCell({
                                loadUrl: `${window._env_.REACT_APP_APIS}/${data.IM.GetLots}`,
                                loadParams: {
                                    ItemId: function() {
                                        return cellElement.data.itemId || null;
                                    }
                                },
                                key: "id",
                                displayExpr: "name",
                                valueExpr: "id",
                                accessToken: accessToken,
                                sort: [{ selector: "name", desc: false }],
                                defaultValue: cellElement.data.lotCode,
                                onValueChangeCallback: (value, text) => {
                                    cellElement.setValue(({ value, text }));
                                },
                            })
                        }
                        setCellValue={(rowData, newValue) => {
                            rowData.lotCode = newValue.text;
                            rowData.lotId = newValue.value;
                        }}
                    />
                    <Column dataField="actualQuantity" caption="Actual Quantity" dataType="number" format="#,##0.00" allowEditing={false} />
                    <Column dataField="amount"  allowEditing={false} visible={false} caption="Item Total" dataType="string"   />
                    <Column dataField="lotDesc1"  allowEditing={false} visible={false} caption="Lot Description1" dataType="string"   />
                    <Column dataField="quantity1" caption="Quantity1"  allowEditing={false} visible={false} dataType="number" format="#,##0.00"  />
                    <Column dataField="quantity2" caption="Quantity2" dataType="number"  allowEditing={false} visible={false} format="#,##0.00"  />
                    <Column dataField="quantity3" caption="Quantity3" dataType="number"  allowEditing={false} visible={false} format="#,##0.00"  />
                    <Column dataField="date1" caption="Date1" dataType="date"  allowEditing={false} visible={false}  />
                    <Column dataField="date2" caption="Date2" dataType="date"  allowEditing={false} visible={false}  />
                    <Column dataField="userDefinedComment" caption="Comment" dataType="string"  allowEditing={false} visible={false}  />
                    <DataGridToolbar>
                        <DataGridToolbarItem name="addRowButton" />
                        <DataGridToolbarItem location="after">
                            <GridLayoutManager gridKey={itemsGridKey} dataGrid={itemsGridRef} setCurrentLayout={setCurrentItemsLayout} />
                        </DataGridToolbarItem>
                        <DataGridToolbarItem name="exportButton" />
                        <DataGridToolbarItem name="columnChooserButton" />
                    </DataGridToolbar>
                </DataGrid>
            </div>
            <StorageChargesGrid
                ref={storageChargesGridRef}
                itemsGridRef={itemsGridRef}
                accessToken={accessToken}
                chargesGridRef={chargesGridRef}
                selectedItemKeys={selectedChargesKeys}
                setSelectedItemKeys={setSelectedChargesKeys}
                currentLayout={currentChargesLayout}
               setCurrentLayout={setCurrentChargesLayout}
            />
        </div>
    );
};

export default StorageChargesTab;

